import React, { useState, useEffect } from "react";
import Select from 'react-select';
import axios from "axios";
import { useNotification } from "../helpers/NotificationContext";

const AddComment = ({ user, player_id, event_id, playersList, parent_comment, edited_comment, setComms, t_controls, onCancelReply, privateTab, setRefreshTrigger, isPlayerPage }) => {
  // Don't render the MAIN form on player's own page
  // Allow rendering if it's an inline form (edit or reply)
  
  const [comment, setComment] = useState(edited_comment?.text || "");
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const { addNotification } = useNotification();

  useEffect(() => {
    // Filter out the current user's player from the options list
    setOptions(
      playersList
        .filter(player => player.id !== user?.id) // Filter out current user
        .map(player => ({
            value: player.id,
            label: `${player.name} ${player.surname}`,
        }))
    );
  },[playersList, user?.id])

  useEffect(() => {
    if (options.length > 0) {
      if (parent_comment) {
        // Don't set recipient to self
        if (parent_comment.user.id !== user?.id) {
          setSelectedOption(options.find(option => option.value === parent_comment.user.id));
        }
      } else if (player_id && player_id !== user?.id) {
        // Don't set recipient to self
        setSelectedOption(options.find(option => option.value === player_id));
      } else if (edited_comment && edited_comment.is_private) {
        setSelectedOption(options.find(option => option.value === edited_comment.recipient_id));
      } else {
        setSelectedOption(null);
      }
    }
  }, [options, parent_comment, player_id, edited_comment, user?.id])

  useEffect(() => {    
    if (edited_comment) {
      setComment(edited_comment.text);
    } else {
      setComment("");
    }
  }, [edited_comment, parent_comment, player_id]);

  const handleSave = () => {
    const token = document.querySelector("[name=csrf-token]")?.content;
    const locale = window.location.pathname.split("/")[1];
    const base = `/${locale}`;
    const url = edited_comment ? `${base}/comments/${edited_comment.id}` : `${base}/comments`;
    const method = edited_comment ? "patch" : "post";
    
    if (privateTab && !selectedOption && !parent_comment && !player_id) {
      addNotification(t_controls.select_recipient || "Please select a recipient for private comments", "error");
      return;
    }

    const isPrivate = privateTab || isPlayerPage;
    
    let recipient_id = null;
    if (isPrivate) {
      if (selectedOption && selectedOption.value) {
        recipient_id = selectedOption.value;
      } else if (parent_comment && parent_comment.user && parent_comment.user.id) {
        recipient_id = parent_comment.user.id;
      } else if (player_id) {
        recipient_id = player_id;
      }
    }

    // Prevent sending private comments to self
    if (isPrivate && recipient_id === user?.id) {
      addNotification(t_controls.cant_send_to_self || "You cannot send private comments to yourself", "error");
      return;
    }
    
    // Validate recipient is selected for private comments
    if (isPrivate && !recipient_id) {
      addNotification(t_controls.select_recipient || "Please select a recipient for private comments", "error");
      return;
    }

    axios({
      method: method,
      url: url,
      data: {
        comment: { 
          text: comment, 
          parent_id: parent_comment ? parent_comment.id : null,
          event_id: event_id ? event_id : null, 
          is_private: isPrivate,
          recipient_id: recipient_id,
        }
      },
      headers: {
        "X-CSRF-TOKEN": token,
        "Content-Type": "application/json"
      }
    })
    .then((response) => {
      setComment("");
      if (!parent_comment && !player_id) {
        setSelectedOption(null);
      }

      if (edited_comment) {
        setComms((prevComments) => prevComments.map((c) => (c.id === edited_comment.id ? response.data.data.comment : c)));
        if (onCancelReply) {
          onCancelReply();
        }
      } else {
        setComms((prevComments) => [...prevComments, response.data.data.comment]);
        if (parent_comment && onCancelReply) {
          onCancelReply();
        }
      }
      // Server-side toast surfaced by the axios response interceptor.
      if (setRefreshTrigger) {
        setRefreshTrigger(prev => prev + 1);
      }
    })
    .catch((error) => {
      // Toast surfaced by the axios response interceptor.
      console.error("Failed to send comment:", error);
    });
  };

  return (
    <div className="alert-form comment-form inset-0">
      <div className="container">
        <div className="form-set">
          <div className="form-header">
            {edited_comment 
              ? <h4>{t_controls.editing_comment}</h4>
              : (parent_comment 
                ? <h4>{t_controls.replying_comment}</h4>
                : <h4>{t_controls.leave_comment}</h4>
              )
            }
          </div>
          
          {(privateTab || (edited_comment && edited_comment.is_private)) && (
            <div className="form-wrap">
              <label>{t_controls.select_player}</label>
              <Select
                options={options}
                value={selectedOption}
                onChange={setSelectedOption}
                placeholder={t_controls.select_player}
                isDisabled={!!parent_comment || !!edited_comment}
                isSearchable
              />
            </div>
          )}

          <div className="form-wrap">
            <textarea className="form-input" id="comment-message" name="message" data-constraints="@Required" placeholder={t_controls.ur_comment} value={comment}
              onChange={(e) => {
                e.persist();
                setComment(e.target.value )
              }}>                
            </textarea>
            <span className="form-validation"></span>
          </div>

          <div className="form-button" style={{ justifyContent: "center" }}>
            <button 
              className={`button button-primary ${(!comment.trim() || (privateTab && !selectedOption && !parent_comment && !player_id)) ? 'disabled' : ''}`} 
              disabled={!comment.trim() || (privateTab && !selectedOption && !parent_comment && !player_id)} 
              type="submit" 
              onClick={() => handleSave()}
            >
              {edited_comment ? t_controls.save : t_controls.send}
            </button>
          
            {(parent_comment?.id || edited_comment) && (
              <button className="button button-primary" style={{marginLeft: "10px", marginTop: "-0px"}} type="submit" onClick={onCancelReply}>{t_controls.cancel_reply}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddComment;
